import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Modal, Input, Button } from 'common/components';
import { emailRegisterOptions } from 'common/constants';
import { useCreateMember } from 'services/hooks/mutations';
import { hasAnyRole, nonEmptyProducts } from '../../products';
import { ProductRolesPicker } from '../ProductRolesPicker';
import { PasswordRequirements, passwordOk } from '../PasswordRequirements';
import { memberErrorMessage } from '../memberErrors';
import { AddUserModalProps, NewUserForm } from './AddUserModal.types';

// Creates a login with a password, with roles by product.
const AddUserModal: React.FC<AddUserModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    onError,
    accountId,
    companyName,
    manageable,
}) => {
    const {
        register,
        handleSubmit: handleFormSubmit,
        control,
        watch,
        reset,
        formState: { errors },
    } = useForm<NewUserForm>({
        defaultValues: { firstName: '', lastName: '', email: '', password: '', products: {} },
    });

    const { createMember, isPending } = useCreateMember();
    const password = watch('password');

    useEffect(() => {
        if (!isOpen) reset();
    }, [isOpen, reset]);

    const onSubmit = async (data: NewUserForm) => {
        if (!passwordOk(data.password)) {
            onError('Password does not meet the requirements');
            return;
        }
        try {
            await createMember({
                accountId,
                data: {
                    first_name: data.firstName,
                    last_name: data.lastName,
                    email: data.email.trim().toLowerCase(),
                    password: data.password,
                    products: nonEmptyProducts(data.products),
                },
            });
            onClose();
            onSuccess('User created successfully!');
        } catch (error: any) {
            console.error('Error creating user:', error);
            onError(memberErrorMessage(error, 'Failed to create user. Please try again.', { emailTaken: true }));
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={companyName ? `Add User to ${companyName}` : 'Add User'}
            size='md'
            showDefaultFooter={false}
        >
            <form onSubmit={handleFormSubmit(onSubmit)} id='add-user-form' className='flex flex-col gap-md'>
                <Input
                    label='First Name'
                    type='text'
                    error={!!errors.firstName}
                    errorText={errors.firstName?.message}
                    {...register('firstName', { required: 'First name is required' })}
                />
                <Input
                    label='Last Name'
                    type='text'
                    error={!!errors.lastName}
                    errorText={errors.lastName?.message}
                    {...register('lastName', { required: 'Last name is required' })}
                />
                <Input
                    label='Email'
                    type='email'
                    error={!!errors.email}
                    errorText={errors.email?.message}
                    {...register('email', emailRegisterOptions)}
                />
                <div className='flex flex-col gap-sm'>
                    <Input
                        label='Password'
                        type='password'
                        showPasswordToggle
                        error={!!errors.password}
                        errorText={errors.password?.message}
                        {...register('password', { required: 'Password is required' })}
                    />
                    <PasswordRequirements password={password} />
                </div>
                <Controller
                    name='products'
                    control={control}
                    rules={{ validate: (v) => hasAnyRole(v) || 'Give at least one role' }}
                    render={({ field }) => (
                        <ProductRolesPicker
                            manageable={manageable}
                            value={field.value}
                            onChange={field.onChange}
                            errorText={errors.products?.message as string | undefined}
                        />
                    )}
                />
                <div className='flex justify-end gap-md mt-xl'>
                    <Button onClick={handleClose} variant='secondary'>
                        Cancel
                    </Button>
                    <Button type='submit' disabled={isPending}>
                        Add User
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default AddUserModal;
